import { useState } from 'react'
import { LoadingButton } from '@mui/lab'
import { Button, Typography } from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { Member } from '@stytch/vanilla-js'
import { OverlayState } from '@/hooks/useOverlay'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'

type RemoveUserDialogProps = {
  overlay: OverlayState
  member: Member | null
  onRemoved?: () => void
}

export default function RemoveUserDialog({
  overlay,
  member,
  onRemoved,
}: RemoveUserDialogProps) {
  const stytch = useStytchB2BClient()
  const [isRemoving, setIsRemoving] = useState(false)

  async function handleRemove() {
    if (!member) return
    setIsRemoving(true)
    try {
      await stytch.organization.members.delete(member.member_id)
      showSuccessSnackbar('User removed successfully.')
      onRemoved?.()
      overlay.close()
    } catch {
      showErrorSnackbar('Failed to remove user. Please try again.')
    } finally {
      setIsRemoving(false)
    }
  }

  return (
    <Dialog
      {...overlay}
      title="Remove User"
      maxWidth="xs"
      disableClickAway={isRemoving}
      disableEscapeKeyDown={isRemoving}
    >
      <DialogContent>
        <Typography>
          You are about to remove{' '}
          <strong>{member?.name || member?.email_address}</strong> from
          Pixydocs. They will no longer be able to sign in. Are you sure?
        </Typography>
      </DialogContent>
      <DialogFooter>
        <Button variant="text" onClick={overlay.close} disabled={isRemoving}>
          Cancel
        </Button>
        <LoadingButton
          loading={isRemoving}
          onClick={handleRemove}
          variant="contained"
          color="error"
        >
          Remove
        </LoadingButton>
      </DialogFooter>
    </Dialog>
  )
}
